// Mirrors the backend server/data_reports/managers/column_name_matching.py so a token validated
// in the browser resolves the same way at report-generation time. A Metabase GUI question names a
// joined column with a U+2192 arrow display name (`Claim → Claim Number`) that no keyboard types,
// while the as-of-date snapshot path keys rows by the compiler alias (`Claim__claim_number`).
// Folding the arrow, an ASCII `->`, underscores and whitespace to one separator collapses those
// forms together. A lone `-` is left untouched so genuinely hyphenated names are not folded.
//
// The Python module is canonical: it decides whether a token resolves at report-generation time,
// and this file only predicts that answer in the browser. Any change to the separator set or the
// ambiguity rule must land there first and be mirrored here, as must any change to the
// trailing-unit-suffix rule; the two test suites assert the rule independently, so neither will
// fail if only one side changes.
const SEPARATOR_RUN = /(?:->|→|[\s_])+/g;

// A trailing parenthesised unit that Metabase appends to a currency-formatted column inside its
// export serializer only, so the generated report's row key is `Total Paid ($)` while the display
// name this file validates against — and the SQL alias the snapshot path sees — are both
// unsuffixed. Two arms: a symbol run optionally prefixed by up to three capitals (`($)`, `(US$)`,
// `(AUD$)`), or a 2-4 letter all-caps run. Three is the widest prefix CLDR carries — across all
// locales only `AUD$`, `NZD$` and `MOP$` reach it, and nothing reaches four. An ordinary
// qualifier like `(Primary)` or a self-join's `(1)` survives, but the
// all-caps arm is not currency-specific (`(ACV)` folds too) and differing units share one key, so
// `Total Paid (EUR)` matches a lone `Total Paid (USD)` column; see the Python module for why both
// costs are accepted. No `g` flag: it is anchored and applied once. The class is `[^A-Za-z0-9\s()]`
// rather than `[^\w\s()]` because Python's `\w` is Unicode-aware and JavaScript's is not, and the
// two engines must fold identically.
const TRAILING_UNIT_SUFFIX = /\s*\(\s*(?:[A-Z]{0,3}[^A-Za-z0-9\s()]{1,3}|[A-Z]{2,4})\s*\)\s*$/;

export const normalizeColumnName = (name: string): string =>
  name.replace(TRAILING_UNIT_SUFFIX, '').replace(SEPARATOR_RUN, ' ').trim().toLowerCase();

// A token matches when its exact (case-insensitive) form matches a column, or its normalized form
// is owned by exactly one column — ambiguous folds are not guessed, matching the backend lookup.
// Owners are counted as distinct names, not as list entries, because the backend collapses them
// into a set: two columns sharing a display name are one owner on both sides.
export const tokenMatchesColumn = (token: string, columnDisplayNames: string[]): boolean => {
  const tokenLower = token.toLowerCase();
  if (columnDisplayNames.some((col) => col.toLowerCase() === tokenLower)) {
    return true;
  }
  const tokenNormalized = normalizeColumnName(token);
  const normalizedOwners = new Set(columnDisplayNames.filter((col) => normalizeColumnName(col) === tokenNormalized));
  return normalizedOwners.size === 1;
};
